import React, { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from 'api/api'
import { buildStreamSignupUrl } from 'components/teams/streamInviteUrl'
import TeamLogo from 'components/shared/icons/TeamLogo'
import styles from 'components/teams/stream-invite.module.scss'

// Survives the signup/login round trip so a returning visitor does not have to
// press Join a second time on the page they already pressed it on.
const PENDING_JOIN_KEY = 'pending_stream_join'

// Host-revenue design handoff: one card. The banner (or its gradient stand-in)
// tops the card, the logo overlaps it and anchors a left-aligned name, Join
// Treechat is the single full-width action, and the "Earn from Good Posts."
// value claim lives in the footer strip instead of the body copy.
export default function StreamInvite({
    streamId: requestedStreamId = null,
    onJoined = null,
    onReadFirst = null,
    presentation = 'page',
    // Why we're asking (e.g. right after the viewer posted here), shown above Join.
    reason = null,
    readFirstLabel = 'Read it first',
}) {
    const { streamId: routeStreamId } = useParams()
    const streamId = requestedStreamId || routeStreamId
    const navigate = useNavigate()
    const { search } = useLocation()
    const [team, setTeam] = useState(null)
    const [status, setStatus] = useState('loading') // loading | ready | disabled | not_found
    const [message, setMessage] = useState('')
    const [showBanner, setShowBanner] = useState(false)
    const [joining, setJoining] = useState(false)
    const isLoggedIn = Boolean(window.gon?.currentUser)
    const invitePageClassName = cn(
        styles.invitePage,
        presentation === 'modal' && styles.inviteModal,
    )

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const preview = await api.getTeamInvitePreview(streamId)
                if (cancelled) return
                if (!preview) {
                    setStatus('disabled')
                    return
                }
                setTeam(preview)
                setStatus('ready')
            } catch (e) {
                setStatus('not_found')
            }
        })()
        return () => {
            cancelled = true
        }
    }, [streamId])

    // Prefetch banner to determine if it exists; only show if it loads.
    // invite_preview now tells us whether one is attached, so skip the probe when not.
    useEffect(() => {
        if (!team?.id || !team?.banner_url) return
        const url = `${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=medium`
        const img = new Image()
        img.onload = () => setShowBanner(true)
        img.onerror = () => setShowBanner(false)
        img.src = url
    }, [team?.id, team?.banner_url])

    const joinNow = useCallback(async () => {
        setMessage('')
        setJoining(true)
        try {
            const res = await api.joinTeam(streamId)
            if (!res) return // 401 → redirected to auth
            if (res?.forbidden) {
                setMessage('Standing invite is disabled for this channel.')
                return
            }
            if (res?.path) {
                if (onJoined) {
                    onJoined({ path: res.path, team })
                } else {
                    navigate(res.path, { replace: true })
                }
            }
        } finally {
            setJoining(false)
        }
    }, [streamId, navigate, onJoined, team])

    // Returning from signup/login: finish the join the visitor already asked for.
    // The flag is cleared unconditionally so a failing join can never loop.
    useEffect(() => {
        if (!isLoggedIn || status !== 'ready') return
        let pending = null
        try {
            pending = sessionStorage.getItem(PENDING_JOIN_KEY)
            if (pending) sessionStorage.removeItem(PENDING_JOIN_KEY)
        } catch (e) {
            pending = null
        }
        if (pending === streamId) joinNow()
    }, [isLoggedIn, status, streamId, joinNow])

    function onAccept() {
        if (isLoggedIn) return joinNow()

        // A stranger clicking Join has no account — send them to signup, not a
        // login form. stream_join makes registration auto-join the stream, so this
        // is the only click they need; the attribution params ride along.
        try {
            sessionStorage.setItem(PENDING_JOIN_KEY, streamId)
        } catch (e) {
            /* private mode — the stream_join param still covers signup */
        }
        window.location.assign(buildStreamSignupUrl(streamId, search))
    }

    // Login has no invite handling of its own, so come back here afterwards and let
    // the auto-join effect finish the job.
    function onLogin() {
        try {
            sessionStorage.setItem(PENDING_JOIN_KEY, streamId)
            sessionStorage.setItem('attempted_path', `/stream/${streamId}/invite${search}`)
        } catch (e) {
            /* private mode — the visitor lands on the invite page and clicks Join */
        }
        window.location.assign('/login')
    }

    if (status === 'loading')
        return (
            <div className={invitePageClassName}>
                <div className={styles.statusCard}>
                    <i className="fa fa-spinner fa-spin" /> Loading invite...
                </div>
            </div>
        )

    if (status === 'disabled')
        return (
            <div className={invitePageClassName}>
                <div className={styles.statusCard}>
                    <div className={styles.statusTitle}>Invite Disabled</div>
                    <div className={styles.statusBody}>
                        This invite link is not enabled for this channel.
                    </div>
                </div>
            </div>
        )

    if (status === 'not_found')
        return (
            <div className={invitePageClassName}>
                <div className={styles.statusCard}>
                    <div className={styles.statusTitle}>Channel Not Found</div>
                    <div className={styles.statusBody}>
                        The channel you are trying to join does not exist.
                    </div>
                </div>
            </div>
        )

    const membersCount = Number(team?.members_count) || 0
    const membersLabel = `${membersCount.toLocaleString()} ${
        membersCount === 1 ? 'member' : 'members'
    }`

    return (
        <div className={invitePageClassName}>
            <div className={styles.card} data-testid="stream-invite-card">
                <div className={styles.banner}>
                    {showBanner && (
                        <img
                            className={styles.bannerImg}
                            src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=medium`}
                            alt="Channel banner"
                        />
                    )}
                </div>

                <div className={styles.cardBody}>
                    <TeamLogo
                        team={team}
                        contextStyles={styles.avatar}
                        initialStyles={styles.avatarInitials}
                    />
                    <div className={styles.teamName}>{team?.name}</div>
                    <div className={styles.metaRow}>
                        <span>{team?.public ? 'Public channel' : 'Private channel'}</span>
                        <span className={styles.metaDot} aria-hidden="true">
                            ·
                        </span>
                        <span>{membersLabel}</span>
                        {team?.host_name && (
                            <span className={styles.metaHost}>
                                <span className={styles.metaDot} aria-hidden="true">
                                    ·
                                </span>
                                <span>hosted by @{team.host_name}</span>
                            </span>
                        )}
                    </div>
                    {team?.description && (
                        <div className={styles.description}>{team?.description}</div>
                    )}
                    {reason && (
                        <div className={styles.inviteReason} data-testid="stream-invite-reason">
                            {reason}
                        </div>
                    )}
                    {message && <div className={styles.notice}>{message}</div>}

                    <div className={styles.actionStack}>
                        <button
                            type="button"
                            className={styles.joinButton}
                            data-testid="stream-invite-join"
                            onClick={onAccept}
                            disabled={joining}
                        >
                            {joining ? 'Joining…' : isLoggedIn ? 'Join' : 'Join Treechat'}
                        </button>
                        {!reason && (
                            <div className={styles.joinBenefit} data-testid="stream-invite-benefit">
                                Its threads show up in your Following.
                            </div>
                        )}
                        <div className={styles.secondaryRow}>
                            {team?.public && (
                                <button
                                    type="button"
                                    className={cn(styles.textAction, styles.readFirst)}
                                    onClick={() =>
                                        onReadFirst
                                            ? onReadFirst()
                                            : navigate(`/stream/${streamId}`)
                                    }
                                >
                                    {readFirstLabel}
                                </button>
                            )}
                            {team?.public && !isLoggedIn && (
                                <span className={styles.secondaryDivider} aria-hidden="true">
                                    |
                                </span>
                            )}
                            {!isLoggedIn && (
                                <span className={styles.loginLine}>
                                    Already on Treechat?{' '}
                                    <button
                                        type="button"
                                        className={styles.loginLink}
                                        data-testid="stream-invite-login"
                                        onClick={onLogin}
                                    >
                                        Log in
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.footerStrip}>Earn from Good Posts.</div>
            </div>
        </div>
    )
}
